import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  CardGiftcard as GiftIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  Star as StarIcon,
  LocalOffer as VoucherIcon,
  Redeem as RedeemIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES } from '../lib/tables';
import { useAuth } from '../context/AuthContext';

export default function Benefits() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [benefits, setBenefits] = useState([]);
  const [usedBenefits, setUsedBenefits] = useState([]);
  const [studentCredits, setStudentCredits] = useState({ total: 0, curricular: 0, extracurricular: 0, transferabil: 0 });
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    let isMounted = true;
    
    const loadDataAsync = async () => {
      if (profile && isMounted) {
        await loadData(isMounted);
      }
    };
    
    loadDataAsync();
    return () => { isMounted = false; };
  }, [profile]);

  const loadData = async (isMounted = true) => {
    setLoading(true);
    try {
      // incarca beneficiile disponibile
      const { data: benefitsData } = await supabase
        .from(TABLES.benefits)
        .select('*')
        .eq('este_activ', true)
        .order('credite_necesare');

      // incarca beneficiile folosite de elev
      const { data: usedData } = await supabase
        .from(TABLES.benefit_usage)
        .select('*, benefits_v2(*)')
        .eq('id_elev', profile.id);

      // credite pe categorii din cererile validate
      const { data: requestsData } = await supabase
        .from(TABLES.credit_requests)
        .select(`
          credite_acordate,
          credit_types_v2(categorie)
        `)
        .eq('id_elev', profile.id)
        .eq('status', 'validat');

      const creditsByCategory = {
        curricular: 0,
        extracurricular: 0,
        transferabil: 0,
      };

      requestsData?.forEach(r => {
        const categorie = r.credit_types_v2?.categorie || 'curricular';
        creditsByCategory[categorie] = (creditsByCategory[categorie] || 0) + (r.credite_acordate || 0);
      });

      const totalCredits = creditsByCategory.curricular + creditsByCategory.extracurricular + creditsByCategory.transferabil;

      // scade creditele folosite
      const usedCredits = usedData?.reduce((sum, u) => sum + (u.credite_utilizate || 0), 0) || 0;

      if (!isMounted) return;

      setBenefits(benefitsData || []);
      setUsedBenefits(usedData || []);
      setStudentCredits({
        total: totalCredits - usedCredits,
        available: totalCredits - usedCredits,
        curricular: creditsByCategory.curricular,
        extracurricular: creditsByCategory.extracurricular,
        transferabil: creditsByCategory.transferabil,
        used: usedCredits,
      });

    } catch (error) {
      console.error('Error loading benefits:', error);
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  const handleClaimBenefit = async () => {
    if (!selectedBenefit) return;
    
    setClaiming(true);
    try {
      // verifica daca are destule credite
      if (studentCredits.available < selectedBenefit.credite_necesare) {
        setSnackbar({ open: true, message: 'Nu ai suficiente credite!', severity: 'error' });
        return;
      }

      // creeaza utilizarea
      const { error } = await supabase
        .from(TABLES.benefit_usage)
        .insert({
          id_beneficiu: selectedBenefit.id,
          id_elev: profile.id,
          credite_utilizate: selectedBenefit.credite_necesare,
        });

      if (error) {
        if (error.code === '23505') {
          setSnackbar({ open: true, message: 'Ai revendicat deja acest beneficiu!', severity: 'warning' });
        } else {
          throw error;
        }
      } else {
        setSnackbar({ open: true, message: 'Beneficiu revendicat cu succes!', severity: 'success' });
        loadData();
      }

    } catch (error) {
      console.error('Error claiming benefit:', error);
      setSnackbar({ open: true, message: 'Eroare la revendicarea beneficiului', severity: 'error' });
    } finally {
      setClaiming(false);
      setDialogOpen(false);
      setSelectedBenefit(null);
    }
  };

  const getBenefitIcon = (tip) => {
    switch (tip) {
      case 'reducere': return <VoucherIcon />;
      case 'voucher': return <GiftIcon />;
      case 'prioritate': return <StarIcon />;
      case 'certificat': return <TrophyIcon />;
      default: return <RedeemIcon />;
    }
  };

  const getBenefitColor = (tip) => {
    switch (tip) {
      case 'reducere': return '#4CAF50';
      case 'voucher': return '#FF9800';
      case 'prioritate': return '#2196F3';
      case 'certificat': return '#9C27B0';
      default: return '#607D8B';
    }
  };

  const isBenefitUsed = (benefitId) => {
    return usedBenefits.some(u => u.id_beneficiu === benefitId);
  };

  const canClaimBenefit = (benefit) => {
    return studentCredits.available >= benefit.credite_necesare && !isBenefitUsed(benefit.id);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
            <TrophyIcon fontSize="large" />
          </Avatar>
          Beneficii pentru Credite
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Schimbă creditele tale pentru beneficii și recompense!
        </Typography>
      </Box>

      {/* Credite disponibile */}
      <Card sx={{ mb: 4, borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2" fontWeight="bold" sx={{ color: 'white' }}>
                  {studentCredits.available}
                </Typography>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Credite Disponibile
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>{studentCredits.curricular}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>Curriculare</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>{studentCredits.extracurricular}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>Extracurriculare</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>{studentCredits.transferabil}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>Transferabile</Typography>
                  </Box>
                </Grid>
              </Grid>
              {studentCredits.used > 0 && (
                <Typography variant="body2" sx={{ display: 'block', mt: 2, color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                  {studentCredits.used} credite utilizate pentru beneficii
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Beneficii utilizate */}
      {usedBenefits.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Beneficii Revendicate
          </Typography>
          <Grid container spacing={2}>
            {usedBenefits.map((usage) => (
              <Grid item xs={12} sm={6} md={4} key={usage.id}>
                <Card sx={{ borderRadius: 2, bgcolor: '#E8F5E9', border: '2px solid #4CAF50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: '#4CAF50' }}>
                        <CheckCircleIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {usage.benefits_v2?.nume_beneficiu}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Revendicat pe {new Date(usage.data_utilizare).toLocaleDateString('ro-RO')}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ my: 3 }} />
        </Box>
      )}

      {/* Lista beneficii disponibile */}
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Beneficii Disponibile
      </Typography>
      
      <Grid container spacing={3}>
        {benefits.map((benefit) => {
          const isUsed = isBenefitUsed(benefit.id);
          const canClaim = canClaimBenefit(benefit);
          const progress = Math.min((studentCredits.available / benefit.credite_necesare) * 100, 100);
          const color = getBenefitColor(benefit.tip_beneficiu);

          return (
            <Grid item xs={12} sm={6} md={4} key={benefit.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  borderRadius: 3,
                  opacity: isUsed ? 0.6 : 1,
                  border: canClaim ? `2px solid ${color}` : '1px solid #e0e0e0',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: canClaim ? 'translateY(-4px)' : 'none',
                    boxShadow: canClaim ? 4 : 1,
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
                      {getBenefitIcon(benefit.tip_beneficiu)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {benefit.nume_beneficiu}
                      </Typography>
                      <Chip 
                        label={benefit.tip_beneficiu} 
                        size="small" 
                        sx={{ bgcolor: `${color}20`, color: color, fontWeight: 'bold' }}
                      />
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                    {benefit.descriere}
                  </Typography>

                  {benefit.valoare_ron && (
                    <Chip 
                      label={`${benefit.valoare_ron} RON`} 
                      color="success" 
                      size="small" 
                      sx={{ mb: 2 }}
                    />
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Credite necesare
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {studentCredits.available}/{benefit.credite_necesare}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={progress} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: progress >= 100 ? '#4CAF50' : color,
                        }
                      }}
                    />
                  </Box>

                  <Button
                    variant={canClaim ? "contained" : "outlined"}
                    fullWidth
                    disabled={isUsed || !canClaim}
                    onClick={() => {
                      setSelectedBenefit(benefit);
                      setDialogOpen(true);
                    }}
                    startIcon={isUsed ? <CheckCircleIcon /> : canClaim ? <RedeemIcon /> : <LockIcon />}
                    sx={{ 
                      bgcolor: canClaim ? color : undefined,
                      '&:hover': { bgcolor: canClaim ? color : undefined, filter: 'brightness(0.9)' }
                    }}
                  >
                    {isUsed ? 'Revendicat' : canClaim ? 'Revendică' : `Încă ${benefit.credite_necesare - studentCredits.available} credite`}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {benefits.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nu există beneficii disponibile momentan.
        </Alert>
      )}

      {/* Dialog confirmare */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirmă Revendicarea
        </DialogTitle>
        <DialogContent>
          {selectedBenefit && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: getBenefitColor(selectedBenefit.tip_beneficiu), width: 80, height: 80, mx: 'auto', mb: 2 }}>
                {getBenefitIcon(selectedBenefit.tip_beneficiu)}
              </Avatar>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {selectedBenefit.nume_beneficiu}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {selectedBenefit.descriere}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Alert severity="warning">
                Vor fi deduse <strong>{selectedBenefit.credite_necesare} credite</strong> din totalul tău.
                După revendicare vei avea <strong>{studentCredits.available - selectedBenefit.credite_necesare} credite</strong> disponibile.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Anulează</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleClaimBenefit}
            disabled={claiming}
          >
            {claiming ? 'Se procesează...' : 'Confirmă Revendicarea'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
